import PickupType from "../../enums/PickupType";
import Game from "../Game";
import BasePickup from "./BasePickup";
import CoinPickup from "./CoinPickup";
import GemPickup from "./GemPickup";
import ApplePickup from "./ApplePickup";
import VacuumPickup from "./VacuumPickup";
import TreasurePickup from "./TreasurePickup";
import CamelChestPickup from "./CamelChestPickup";
import SpatialGrid from "../SpatialGrid";
import { getCurrentPerformanceConfig } from "../../../utils/performance";

export interface PickupGroupConfig {
  maxSize?: { [key in PickupType]?: number };
}

export default class PickupGroup extends Phaser.GameObjects.Group {
  stored: { [key: string]: BasePickup[] };
  spawned: { [key: string]: BasePickup[] };
  maxSizes: { [key: string]: number };
  private spatialGrid: SpatialGrid<BasePickup>;

  constructor(scene: Phaser.Scene, config?: PickupGroupConfig) {
    super(scene);
    this.stored = {};
    this.spawned = {};
    this.maxSizes = {};
    this.spatialGrid = new SpatialGrid<BasePickup>(120); // 120px cell size optimized for gem consolidation distance

    // Set up off-screen detection for pickup priority
    this.spatialGrid.setOffScreenCheck((pickup) => {
      return Game.Core.containmentRect_Screen
        ? !Game.Core.containmentRect_Screen.Contains(pickup)
        : false;
    });

    this.Init();

    for (const pickup in PickupType) {
      this.stored[pickup] = [];
      this.spawned[pickup] = [];
      // Set max size from config or use defaults
      this.maxSizes[pickup] =
        config?.maxSize?.[pickup as PickupType] || Number.MAX_SAFE_INTEGER;
    }
  }

  Init() {
    this.scene.add.existing(this);
  }

  SpawnAt(x: number, y: number, pickup: PickupType) {
    const pickupObj = this.Spawn(pickup) as BasePickup;
    this.scene.children.add(pickupObj);
    pickupObj.setPosition(x, y);
    pickupObj.OnRecycle();

    // Add to spatial grid
    this.spatialGrid.addObject(pickupObj);

    return pickupObj;
  }

  Spawn(pickup: PickupType) {
    // Check if we need to cull before spawning
    this.enforceMaxSize(pickup);

    let pickupObj;
    if (this.stored[pickup]) {
      pickupObj = this.stored[pickup].pop();
    } else {
      this.stored[pickup] = [];
      this.spawned[pickup] = [];
    }
    if (!pickupObj) {
      pickupObj = this.Make(pickup);
      pickupObj.Init();
    }
    this.add(pickupObj, false);
    this.spawned[pickup].push(pickupObj);
    Game.Core.PickupGroup.add(pickupObj, false);
    return pickupObj;
  }

  private enforceMaxSize(pickupType: PickupType) {
    // Never enforce size limits on treasures - they are too important
    if (
      pickupType === PickupType.TREASURE ||
      pickupType === PickupType.CAMEL_CHEST
    ) {
      return;
    }

    const spawned = this.spawned[pickupType];
    const maxSize = this.maxSizes[pickupType];

    if (!spawned || spawned.length < maxSize) {
      return;
    }

    // Special distance-based cleanup for coins to preserve reachable ones
    if (pickupType === PickupType.COIN) {
      this.enforceDistanceBasedCleanupForCoins();
      return;
    }

    // Standard enforcement for other pickup types
    const toRemove = spawned.length - maxSize + 1; // +1 for the new item we're about to spawn

    for (let i = 0; i < toRemove; i++) {
      const victimPickup =
        spawned.length > 0 ? this.getFurthestFromPlayer(spawned) : null;
      if (victimPickup) {
        // Use polymorphism - let each pickup type handle max size enforcement its own way
        // BasePickup: simple culling
        // GemPickup: consolidation before culling
        victimPickup.handleMaxSizeEnforcement(spawned);
      }
    }
  }

  private getFurthestFromPlayer(spawned: BasePickup[]): BasePickup | null {
    if (!Game.Core.Player || spawned.length === 0) return spawned[0] || null;

    // Use spatial grid for efficient furthest search
    return this.spatialGrid.findFurthest(
      Game.Core.Player.x,
      Game.Core.Player.y,
      spawned,
      true // prioritizeOffScreen
    );
  }

  Return(pickup: BasePickup) {
    // Remove from spatial grid
    this.spatialGrid.removeObject(pickup);

    this.scene.children.remove(pickup);
    this.remove(pickup, true, false);
    this.spawned[pickup.itemType].splice(
      this.spawned[pickup.itemType].indexOf(pickup),
      1
    );
    Game.Core.PickupGroup.remove(pickup, true);
    this.stored[pickup.itemType].push(pickup);
  }

  /**
   * Update pickup position in spatial grid
   * Should be called when a pickup moves significantly
   */
  updatePickupPosition(pickup: BasePickup, newX: number, newY: number): void {
    // Update pickup position first, then update in spatial grid
    pickup.setPosition(newX, newY);
    this.spatialGrid.updateObjectPosition(pickup, newX, newY);
  }

  /**
   * Find pickups near a point efficiently using spatial grid
   */
  findNearbyPickups(
    x: number,
    y: number,
    radius: number,
    pickupType?: PickupType
  ): BasePickup[] {
    return this.spatialGrid.findInRadius(x, y, radius, {
      filterFunction: pickupType
        ? (pickup) => pickup.itemType === pickupType
        : undefined,
    });
  }

  /**
   * Distance-based cleanup specifically for coins when maxSize is reached
   * Removes coins that are far from player to make room for new ones
   * Uses performance-based thresholds for optimal device-specific behavior
   */
  private enforceDistanceBasedCleanupForCoins(): void {
    const coinSpawned = this.spawned[PickupType.COIN];
    const maxSize = this.maxSizes[PickupType.COIN];

    if (!coinSpawned || coinSpawned.length < maxSize) {
      return;
    }

    // Get performance-based cleanup parameters
    const performanceConfig = getCurrentPerformanceConfig();
    const unreachableDistance = performanceConfig.unreachableDistance;
    const minCoinsToKeep = performanceConfig.minCoinsToKeep;

    if (!Game.Core.Player) {
      return;
    }

    const playerX = Game.Core.Player.x;
    const playerY = Game.Core.Player.y;

    // Find coins that are far from the player
    const distantCoins: BasePickup[] = [];

    for (const coin of coinSpawned) {
      const distance = Phaser.Math.Distance.Between(
        playerX,
        playerY,
        coin.x,
        coin.y
      );

      if (distance > unreachableDistance) {
        distantCoins.push(coin);
      }
    }

    // Sort by distance (furthest first)
    distantCoins.sort((a, b) => {
      const distA = Phaser.Math.Distance.Between(playerX, playerY, a.x, a.y);
      const distB = Phaser.Math.Distance.Between(playerX, playerY, b.x, b.y);
      return distB - distA;
    });

    // Calculate how many coins to remove
    const excessCount = coinSpawned.length - minCoinsToKeep;
    const toRemoveCount = Math.min(
      distantCoins.length,
      Math.max(0, excessCount),
      10 // Don't remove more than 10 coins at once to avoid noticeable gaps
    );

    // Remove the furthest coins
    for (let i = 0; i < toRemoveCount; i++) {
      const coin = distantCoins[i];
      if (coin.DeSpawn) {
        coin.DeSpawn();
      }
    }
  }

  /**
   * Clear spatial grid (call when clearing the group)
   */
  clearAll(): void {
    super.clear();
    this.spatialGrid.clear();

    // Clear all arrays
    for (const pickup in PickupType) {
      this.stored[pickup] = [];
      this.spawned[pickup] = [];
    }
  }

  Make(pickup: PickupType): BasePickup {
    switch (pickup) {
      default:
      case PickupType.COIN:
        return new CoinPickup(this, 0, 0);
      case PickupType.GEM:
        return new GemPickup(this, 0, 0);
      case PickupType.APPLE:
        return new ApplePickup(this, 0, 0);
      case PickupType.VACUUM:
        return new VacuumPickup(this, 0, 0);
      case PickupType.TREASURE:
        return new TreasurePickup(this, 0, 0);
      case PickupType.CAMEL_CHEST:
        return new CamelChestPickup(this, 0, 0);
    }
  }
}
